import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import type {
  Addon,
  AddonCategoryEntity,
  AddonCategoryWithCount,
  CompanyAddon,
  CompanyAddonWithAddon,
  InsertCompanyAddon,
} from '@shared/types/database';
import { toast } from 'react-hot-toast';
import { provisionAddonSideEffects } from './_addon-side-effects';

// =====================================================
// Type Definitions
// =====================================================

interface AuditLogUser {
  id: string;
  full_name: string;
  email: string;
}

interface AddonAuditLogEntry {
  id: string;
  addon_id: string;
  changed_by: string;
  changed_at: string;
  change_type: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  changed_by_user: AuditLogUser | null;
}

interface AddonCategoryAuditLogEntry {
  id: string;
  category_id: string;
  changed_by: string;
  changed_at: string;
  change_type: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  changed_by_user: AuditLogUser | null;
}

// =====================================================
// Fetch Addons (Global Catalog)
// =====================================================

/**
 * Fetch all available addons from the global catalog
 */
export function useAddons() {
  return useQuery({
    queryKey: ['addons'],
    queryFn: async (): Promise<Addon[]> => {
      const { data, error } = await supabase
        .from('addons')
        .select('*')
        .is('deleted_at', null)
        .eq('is_active', true)
        .order('is_coming_soon', { ascending: true }) // Live add-ons first (false < true)
        .order('is_featured', { ascending: false })
        .order('name');

      if (error) {
        console.error('[useAddons] Error:', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch a single addon by ID
 */
export function useAddon(id: string) {
  return useQuery({
    queryKey: ['addon', id],
    queryFn: async (): Promise<Addon | null> => {
      const { data, error } = await supabase
        .from('addons')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .single();

      if (error) {
        console.error('[useAddon] Error:', error);
        throw error;
      }

      return data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch addon by slug
 */
export function useAddonBySlug(slug: string) {
  return useQuery({
    queryKey: ['addon', 'slug', slug],
    queryFn: async (): Promise<Addon | null> => {
      const { data, error } = await supabase
        .from('addons')
        .select('*')
        .eq('slug', slug)
        .is('deleted_at', null)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          return null;
        }
        console.error('[useAddonBySlug] Error:', error);
        throw error;
      }

      return data;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

// =====================================================
// Fetch Company Addons (Per-Tenant)
// =====================================================

/**
 * Fetch all enabled addons for the current company
 */
export function useCompanyAddons() {
  return useQuery({
    queryKey: ['company-addons'],
    queryFn: async (): Promise<CompanyAddonWithAddon[]> => {
      const { data, error } = await supabase
        .from('company_addons')
        .select(`
          *,
          addon:addons(*)
        `)
        .is('deleted_at', null)
        .order('enabled_at', { ascending: false });

      if (error) {
        console.error('[useCompanyAddons] Error:', error);
        throw error;
      }

      return (data || []) as CompanyAddonWithAddon[];
    },
    staleTime: 30 * 1000, // Cache for 30 seconds (shorter for cross-tab sync)
    // Override global setting: refetch when user returns to tab
    // This enables cross-tab sync when addon is activated in another tab
    refetchOnWindowFocus: 'always', // Always refetch, even if data is fresh
  });
}

/**
 * Fetch all enabled addon slugs for the current company as a Set.
 * Shares the same cache key as useCompanyAddons so no extra network request
 * is made when both hooks are used on the same page.
 */
export function useEnabledAddons() {
  return useQuery({
    queryKey: ['company-addons'],
    queryFn: async (): Promise<CompanyAddonWithAddon[]> => {
      const { data, error } = await supabase
        .from('company_addons')
        .select(`
          *,
          addon:addons(*)
        `)
        .is('deleted_at', null)
        .order('enabled_at', { ascending: false });

      if (error) {
        console.error('[useEnabledAddons] Error:', error);
        throw error;
      }

      return (data || []) as CompanyAddonWithAddon[];
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: 'always',
    select: (addons) =>
      new Set(
        addons
          .filter((ca) => ca.is_enabled && ca.addon?.slug)
          .map((ca) => ca.addon!.slug as string)
      ),
  });
}

/**
 * Check if a specific addon is enabled for the current company
 */
export function useIsAddonEnabled(slug: string) {
  const { data: enabledAddons, isLoading } = useEnabledAddons();

  return {
    isEnabled: enabledAddons?.has(slug) ?? false,
    isLoading,
  };
}

/**
 * Check if a platform-managed addon is enabled (uses credits instead of BYOK)
 * Platform addons have slugs ending with '-platform'
 */
export function useIsPlatformAddonEnabled(serviceSlug: string) {
  const platformSlug = `${serviceSlug}-platform`;
  const { isEnabled, isLoading } = useIsAddonEnabled(platformSlug);

  return { isPlatformEnabled: isEnabled, isLoading };
}

/**
 * Get addon settings for the current company
 */
export function useAddonSettings(slug: string) {
  const { data: companyAddons, isLoading } = useCompanyAddons();

  const companyAddon = companyAddons?.find(
    (ca) => ca.addon?.slug === slug && ca.is_enabled
  );

  return {
    settings: companyAddon?.settings || {},
    companyAddonId: companyAddon?.id,
    isLoading,
  };
}

// =====================================================
// Mutations
// =====================================================

/**
 * Enable an addon for the current company
 * Also auto-provisions brand voices for certain addons (Phase 43)
 */
export function useEnableAddon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      addon_id: string;
      company_id: string;
      settings?: Record<string, unknown>;
      enabled_by: string;
    }): Promise<CompanyAddon & { addon_slug?: string }> => {
      // First get the addon slug for auto-provisioning
      const { data: addonData } = await supabase
        .from('addons')
        .select('slug')
        .eq('id', data.addon_id)
        .single();

      // Check if record exists (including soft-deleted ones)
      const { data: existingRecord } = await supabase
        .from('company_addons')
        .select('*')
        .eq('company_id', data.company_id)
        .eq('addon_id', data.addon_id)
        .maybeSingle();

      const now = new Date().toISOString();

      let result: CompanyAddon;

      if (existingRecord) {
        // Update existing record (re-enable if soft-deleted)
        const { data: updateResult, error } = await supabase
          .from('company_addons')
          .update({
            is_enabled: true,
            settings: data.settings || existingRecord.settings,
            enabled_by: data.enabled_by,
            enabled_at: now,
            deleted_at: null, // Clear soft delete
          })
          .eq('id', existingRecord.id)
          .select()
          .single();

        if (error) {
          console.error('[useEnableAddon] Error updating:', error);
          throw error;
        }

        result = updateResult;
      } else {
        // Insert new record
        const insertData: InsertCompanyAddon = {
          company_id: data.company_id,
          addon_id: data.addon_id,
          is_enabled: true,
          settings: data.settings || {},
          enabled_by: data.enabled_by,
          enabled_at: now,
          last_used_at: null,
        };

        const { data: insertResult, error } = await supabase
          .from('company_addons')
          .insert(insertData)
          .select()
          .single();

        if (error) {
          console.error('[useEnableAddon] Error inserting:', error);
          throw error;
        }

        result = insertResult;
      }

      // Return with addon slug for onSuccess
      return { ...result, addon_slug: addonData?.slug };
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['company-addons'] });

      // Post-enable side effects (mission-control pipeline seed, addon voice provisioning).
      // Same logic runs in useGrantAddon for parity.
      const addonSlug = data.addon_slug;
      const { data: { user } } = await supabase.auth.getUser();
      if (addonSlug && user) {
        try {
          const { invalidateKeys } = await provisionAddonSideEffects({
            companyId: data.company_id,
            addonSlug,
            actorUserId: user.id,
          });
          for (const key of invalidateKeys) {
            queryClient.invalidateQueries({ queryKey: key });
          }
        } catch (error) {
          console.error('[useEnableAddon] Side-effects error:', error);
        }
      }

      // Phase 166 D-20: trigger 90-day theme backfill when action-loop is enabled.
      // Fire-and-forget; the sentiment-themes-cron picks up the enqueued job on its next tick.
      // Errors are warned only - backfill failing must never block the addon enable flow.
      if (addonSlug === 'action-loop') {
        void (async () => {
          try {
            const { data: sessionData } = await supabase.auth.refreshSession();
            const token = sessionData?.session?.access_token;
            if (!token) return;
            const workersUrl = import.meta.env.VITE_WORKERS_URL ?? '';
            const res = await fetch(`${workersUrl}/v1/sentiment/enqueue-backfill`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            if (!res.ok) {
              console.warn('[useEnableAddon] backfill enqueue failed:', res.status, await res.text());
            }
          } catch (err) {
            console.warn('[useEnableAddon] backfill enqueue threw:', err);
          }
        })();
      }

      toast.success('Add-on enabled successfully');
    },
    onError: (error) => {
      console.error('[useEnableAddon] Mutation error:', error);
      toast.error('Failed to enable add-on');
    },
  });
}

/**
 * Disable an addon for the current company (soft delete)
 */
export function useDisableAddon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyAddonId: string): Promise<void> => {
      const { error } = await supabase
        .from('company_addons')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', companyAddonId);

      if (error) {
        console.error('[useDisableAddon] Error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-addons'] });
      toast.success('Add-on disabled successfully');
    },
    onError: (error) => {
      console.error('[useDisableAddon] Mutation error:', error);
      toast.error('Failed to disable add-on');
    },
  });
}

/**
 * Toggle addon enabled/disabled state
 */
export function useToggleAddon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      company_addon_id: string;
      is_enabled: boolean;
    }): Promise<CompanyAddon> => {
      const { data: result, error } = await supabase
        .from('company_addons')
        .update({ is_enabled: data.is_enabled })
        .eq('id', data.company_addon_id)
        .select()
        .single();

      if (error) {
        console.error('[useToggleAddon] Error:', error);
        throw error;
      }

      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['company-addons'] });
      const status = data.is_enabled ? 'enabled' : 'disabled';
      toast.success(`Add-on ${status} successfully`);
    },
    onError: (error) => {
      console.error('[useToggleAddon] Mutation error:', error);
      toast.error('Failed to toggle add-on');
    },
  });
}

/**
 * Update addon settings for the current company
 */
export function useUpdateAddonSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      company_addon_id: string;
      settings: Record<string, unknown>;
    }): Promise<CompanyAddon> => {
      const { data: result, error } = await supabase
        .from('company_addons')
        .update({ settings: data.settings })
        .eq('id', data.company_addon_id)
        .select()
        .single();

      if (error) {
        console.error('[useUpdateAddonSettings] Error:', error);
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-addons'] });
      toast.success('Add-on settings updated successfully');
    },
    onError: (error) => {
      console.error('[useUpdateAddonSettings] Mutation error:', error);
      toast.error('Failed to update add-on settings');
    },
  });
}

/**
 * Update last_used_at timestamp for an addon
 */
export function useTrackAddonUsage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyAddonId: string): Promise<void> => {
      const { error } = await supabase
        .from('company_addons')
        .update({ last_used_at: new Date().toISOString() })
        .eq('id', companyAddonId);

      if (error) {
        console.error('[useTrackAddonUsage] Error:', error);
        // Don't throw - usage tracking is not critical
      }
    },
    onSuccess: () => {
      // Silently invalidate cache
      queryClient.invalidateQueries({ queryKey: ['company-addons'] });
    },
  });
}

// =====================================================
// Superadmin: Addon Catalog Management
// =====================================================

/**
 * Check if current user is superadmin
 */
export function useIsSuperadmin() {
  return useQuery({
    queryKey: ['is-superadmin'],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc('is_super_admin');

      if (error) {
        console.error('[useIsSuperadmin] Error:', error);
        return false;
      }

      return data || false;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch all addons (including inactive) for superadmin management
 */
export function useAllAddons() {
  return useQuery({
    queryKey: ['addons', 'all'],
    queryFn: async (): Promise<Addon[]> => {
      const { data, error } = await supabase
        .from('addons')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[useAllAddons] Error:', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Create a new addon in the catalog
 */
export function useCreateAddon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      slug: string;
      name: string;
      description: string;
      long_description?: string;
      category: string;
      version?: string;
      icon_url?: string;
      tags?: string[];
      developer_name?: string;
      developer_email?: string;
      developer_website?: string;
      is_active?: boolean;
      is_featured?: boolean;
      is_coming_soon?: boolean;
      requires_api_key?: boolean;
      api_provider?: string;
      settings_schema?: Record<string, unknown>;
      // Phase 160 v1.7 - display-only paid-addon catalog fields
      is_paid?: boolean;
      price_per_location_eur?: number | null;
      billing_cycle?: 'monthly' | 'yearly' | null;
    }): Promise<Addon> => {
      const { data: result, error } = await supabase
        .from('addons')
        .insert({
          ...data,
          version: data.version || '1.0.0',
          is_active: data.is_active ?? true,
          is_featured: data.is_featured ?? false,
          is_coming_soon: data.is_coming_soon ?? false,
          requires_api_key: data.requires_api_key ?? false,
          is_paid: data.is_paid ?? false,
        })
        .select()
        .single();

      if (error) {
        console.error('[useCreateAddon] Error:', error);
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addons'] });
      toast.success('Add-on created successfully');
    },
    onError: (error: unknown) => {
      console.error('[useCreateAddon] Mutation error:', error);
      if (error.code === '23505') {
        toast.error('An add-on with this slug already exists');
      } else {
        toast.error('Failed to create add-on');
      }
    },
  });
}

/**
 * Update an existing addon in the catalog
 */
export function useUpdateAddon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      id: string;
      updates: Partial<Addon>;
      user_id: string;
    }): Promise<Addon> => {
      const { data: result, error } = await supabase
        .from('addons')
        .update({
          ...data.updates,
          updated_by: data.user_id,
        })
        .eq('id', data.id)
        .select()
        .single();

      if (error) {
        console.error('[useUpdateAddon] Error:', error);
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addons'] });
      toast.success('Add-on updated successfully');
    },
    onError: (error: unknown) => {
      console.error('[useUpdateAddon] Mutation error:', error);
      toast.error('Failed to update add-on');
    },
  });
}

/**
 * Delete an addon from the catalog (soft delete)
 */
export function useDeleteAddon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (addonId: string): Promise<void> => {
      const { error } = await supabase
        .from('addons')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', addonId);

      if (error) {
        console.error('[useDeleteAddon] Error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addons'] });
      toast.success('Add-on deleted successfully');
    },
    onError: (error) => {
      console.error('[useDeleteAddon] Mutation error:', error);
      toast.error('Failed to delete add-on');
    },
  });
}

/**
 * Upload addon logo to Supabase Storage
 */
export function useUploadAddonLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      addonId: string;
      addonSlug: string;
      file: File;
      userId: string;
    }): Promise<string> => {
      const { addonId, addonSlug, file, userId } = data;

      // Validate file size (max 1MB for logos)
      if (file.size > 1 * 1024 * 1024) {
        throw new Error('File size must be less than 1MB');
      }

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('File must be PNG, JPG, or SVG');
      }

      // Get file extension
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const filePath = `${addonSlug}/logo.${ext}`;

      // Upload to storage (upsert mode to replace existing logo)
      const { error: uploadError } = await supabase.storage
        .from('addon-logos')
        .upload(filePath, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: '604800', // 7 days - enables CDN caching
        });

      if (uploadError) {
        console.error('[useUploadAddonLogo] Upload error:', uploadError);
        throw uploadError;
      }

      // Get public URL with cache-busting query parameter
      const { data: { publicUrl } } = supabase.storage
        .from('addon-logos')
        .getPublicUrl(filePath);

      // Add timestamp to bust browser cache
      const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;

      // Update addon icon_url in database
      const { error: updateError } = await supabase
        .from('addons')
        .update({
          icon_url: cacheBustedUrl,
          updated_by: userId,
        })
        .eq('id', addonId);

      if (updateError) {
        console.error('[useUploadAddonLogo] Database update error:', updateError);
        // Try to delete uploaded file on failure
        await supabase.storage.from('addon-logos').remove([filePath]);
        throw updateError;
      }

      return cacheBustedUrl;
    },
    onSuccess: () => {
      // Invalidate all addon queries to refresh the catalog
      queryClient.invalidateQueries({ queryKey: ['addons'] });
      toast.success('Addon logo uploaded successfully');
    },
    onError: (error: unknown) => {
      console.error('[useUploadAddonLogo] Mutation error:', error);
      toast.error(error.message || 'Failed to upload addon logo');
    },
  });
}

/**
 * Fetch audit log for an addon
 */
export function useAddonAuditLog(addonId: string) {
  return useQuery({
    queryKey: ['addon-audit-log', addonId],
    queryFn: async (): Promise<AddonAuditLogEntry[]> => {
      const { data, error } = await supabase
        .from('addon_audit_log')
        .select(`
          *,
          changed_by_user:users!addon_audit_log_changed_by_fkey(
            id,
            full_name,
            email
          )
        `)
        .eq('addon_id', addonId)
        .order('changed_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('[useAddonAuditLog] Error:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!addonId,
    staleTime: 60 * 1000, // Cache for 1 minute
  });
}

// =====================================================
// Addon Categories
// =====================================================

/**
 * Fetch all addon categories (active only for regular users)
 * Returns empty array if the addon_categories table doesn't exist yet (migration not run)
 */
export function useAddonCategories() {
  return useQuery({
    queryKey: ['addon-categories'],
    queryFn: async (): Promise<AddonCategoryEntity[]> => {
      const { data, error } = await supabase
        .from('addon_categories')
        .select('*')
        .is('deleted_at', null)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        // Table might not exist yet if migration hasn't been run
        // Return empty array to allow backwards compatibility
        if (error.code === '42P01' || error.message?.includes('relation') || error.message?.includes('does not exist')) {
          console.warn('[useAddonCategories] addon_categories table does not exist yet. Run the migration.');
          return [];
        }
        console.error('[useAddonCategories] Error:', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch all addon categories (including inactive) for superadmin management
 * Returns empty array if the addon_categories table doesn't exist yet (migration not run)
 */
export function useAllAddonCategories() {
  return useQuery({
    queryKey: ['addon-categories', 'all'],
    queryFn: async (): Promise<AddonCategoryWithCount[]> => {
      // First get all categories
      const { data: categories, error: catError } = await supabase
        .from('addon_categories')
        .select('*')
        .is('deleted_at', null)
        .order('sort_order', { ascending: true });

      if (catError) {
        // Table might not exist yet if migration hasn't been run
        if (catError.code === '42P01' || catError.message?.includes('relation') || catError.message?.includes('does not exist')) {
          console.warn('[useAllAddonCategories] addon_categories table does not exist yet. Run the migration.');
          return [];
        }
        console.error('[useAllAddonCategories] Error fetching categories:', catError);
        throw catError;
      }

      // Then get addon counts per category
      const { data: addons, error: addonError } = await supabase
        .from('addons')
        .select('category_id')
        .is('deleted_at', null);

      if (addonError) {
        // category_id column might not exist yet
        if (addonError.message?.includes('category_id')) {
          console.warn('[useAllAddonCategories] category_id column does not exist yet. Run the migration.');
          // Return categories without counts
          return (categories || []).map((cat) => ({
            ...cat,
            addon_count: 0,
          }));
        }
        console.error('[useAllAddonCategories] Error fetching addons:', addonError);
        throw addonError;
      }

      // Count addons per category
      const countMap = new Map<string, number>();
      addons?.forEach((addon) => {
        if (addon.category_id) {
          countMap.set(addon.category_id, (countMap.get(addon.category_id) || 0) + 1);
        }
      });

      // Merge counts with categories
      const categoriesWithCount: AddonCategoryWithCount[] = (categories || []).map((cat) => ({
        ...cat,
        addon_count: countMap.get(cat.id) || 0,
      }));

      return categoriesWithCount;
    },
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });
}

/**
 * Fetch a single addon category by ID
 */
export function useAddonCategory(id: string) {
  return useQuery({
    queryKey: ['addon-category', id],
    queryFn: async (): Promise<AddonCategoryEntity | null> => {
      const { data, error } = await supabase
        .from('addon_categories')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .single();

      if (error) {
        console.error('[useAddonCategory] Error:', error);
        throw error;
      }

      return data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Create a new addon category
 */
export function useCreateAddonCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      slug: string;
      description?: string;
      icon?: string;
      color?: string;
      sort_order?: number;
      is_active?: boolean;
    }): Promise<AddonCategoryEntity> => {
      const { data: result, error } = await supabase
        .from('addon_categories')
        .insert({
          name: data.name,
          slug: data.slug,
          description: data.description || null,
          icon: data.icon || null,
          color: data.color || 'gray',
          sort_order: data.sort_order ?? 0,
          is_active: data.is_active ?? true,
        })
        .select()
        .single();

      if (error) {
        console.error('[useCreateAddonCategory] Error:', error);
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addon-categories'] });
      toast.success('Category created successfully');
    },
    onError: (error: unknown) => {
      console.error('[useCreateAddonCategory] Mutation error:', error);
      if (error.code === '23505') {
        toast.error('A category with this slug already exists');
      } else {
        toast.error('Failed to create category');
      }
    },
  });
}

/**
 * Update an existing addon category
 */
export function useUpdateAddonCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      id: string;
      updates: Partial<Omit<AddonCategoryEntity, 'id' | 'created_at' | 'updated_at' | 'deleted_at'>>;
    }): Promise<AddonCategoryEntity> => {
      const { data: result, error } = await supabase
        .from('addon_categories')
        .update(data.updates)
        .eq('id', data.id)
        .select()
        .single();

      if (error) {
        console.error('[useUpdateAddonCategory] Error:', error);
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addon-categories'] });
      toast.success('Category updated successfully');
    },
    onError: (error: unknown) => {
      console.error('[useUpdateAddonCategory] Mutation error:', error);
      if (error.code === '23505') {
        toast.error('A category with this slug already exists');
      } else {
        toast.error('Failed to update category');
      }
    },
  });
}

/**
 * Delete an addon category (soft delete)
 */
export function useDeleteAddonCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryId: string): Promise<void> => {
      // First check if any addons use this category
      const { data: addons, error: checkError } = await supabase
        .from('addons')
        .select('id')
        .eq('category_id', categoryId)
        .is('deleted_at', null)
        .limit(1);

      if (checkError) {
        console.error('[useDeleteAddonCategory] Check error:', checkError);
        throw checkError;
      }

      if (addons && addons.length > 0) {
        throw new Error('Cannot delete category that has addons. Please reassign addons first.');
      }

      const { error } = await supabase
        .from('addon_categories')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', categoryId);

      if (error) {
        console.error('[useDeleteAddonCategory] Error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addon-categories'] });
      toast.success('Category deleted successfully');
    },
    onError: (error: unknown) => {
      console.error('[useDeleteAddonCategory] Mutation error:', error);
      toast.error(error.message || 'Failed to delete category');
    },
  });
}

/**
 * Reorder addon categories (bulk update sort_order)
 */
export function useReorderAddonCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: string; sort_order: number }[]): Promise<void> => {
      // Update each category's sort_order
      const updates = data.map(({ id, sort_order }) =>
        supabase
          .from('addon_categories')
          .update({ sort_order })
          .eq('id', id)
      );

      const results = await Promise.all(updates);

      // Check for errors
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) {
        console.error('[useReorderAddonCategories] Errors:', errors);
        throw new Error('Failed to reorder some categories');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addon-categories'] });
      toast.success('Categories reordered successfully');
    },
    onError: (error) => {
      console.error('[useReorderAddonCategories] Mutation error:', error);
      toast.error('Failed to reorder categories');
    },
  });
}

/**
 * Fetch audit log for a category
 */
export function useAddonCategoryAuditLog(categoryId: string) {
  return useQuery({
    queryKey: ['addon-category-audit-log', categoryId],
    queryFn: async (): Promise<AddonCategoryAuditLogEntry[]> => {
      const { data, error } = await supabase
        .from('addon_category_audit_log')
        .select(`
          *,
          changed_by_user:users!addon_category_audit_log_changed_by_fkey(
            id,
            full_name,
            email
          )
        `)
        .eq('category_id', categoryId)
        .order('changed_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('[useAddonCategoryAuditLog] Error:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!categoryId,
    staleTime: 60 * 1000,
  });
}
